import mapValues from 'lodash/mapValues';

import MedicationAction from './controls/MedicationAction';
import InfusionAction from './controls/InfusionAction';
import OxygenAction from './controls/OxygenAction';
import DialogueAction from './controls/DialogueAction';
import CallAction from './controls/CallAction';
import AiDialogueAction from './controls/AiDialogueAction.vue';
import NpcAction from './controls/NpcAction.vue';
import IvPlacedAction from './controls/IvPlacedAction';
import InterventionAction from './controls/InterventionAction';
import CompressionAction from './controls/CompressionAction';
import MonitorVitalsAction from './controls/MonitorVitalsAction';
import MonitorLeadsAction from './controls/MonitorLeadsAction';
import TakeVitalAction from './controls/TakeVitalAction';
import PacingAction from './controls/PacingAction';
import DefibAction from './controls/DefibAction';
import VagalManeuverAction from './controls/VagalManeuverAction';
import PenlightAction from './controls/PenlightAction';
import NasalSwabAction from './controls/NasalSwabAction';
import ExposeAction from './controls/ExposeAction';
import PatientTransferAction from './controls/PatientTransferAction';
import PPEAction from './controls/PPEAction';
import BedRailAction from './controls/BedRailAction';
import StethoscopeAction from './controls/StethoscopeAction';
import PalpatePulseAction from './controls/PalpatePulseAction';
import SpinalAssessmentAction from './controls/SpinalAssessmentAction';
import CustomMenuAction from './controls/CustomMenuAction';
import GlucoseAction from './controls/GlucoseAction';
import CustomStateEvent from './controls/CustomStateEvent';
import PhysicalAssessmentAction from './controls/PhysicalAssessmentAction';
import makeVueControl from './utils/makeVueControl';
import getEnabledRelations from './utils/getEnabledRelations';
import * as ControlUtils from './utils/controlUtils';

const ACTIONS = {
  QUESTION: {
    title: 'Question',
    label: 'When question asked',
    component: DialogueAction,
    options: {
      props: {
        actionType: 'QUESTION',
        placeholder: 'Question',
        getDialogueItems: (caseConf) => caseConf.dialogue && caseConf.dialogue.questions
      }
    },
    caseSpecific: true,
    isRecommended: ControlUtils.areQuestionsRecommended,
    isDisabled: ControlUtils.isAiEnabled,
    category: 'Dialogue',
    superCategory: 'Assess',
  },
  AI_QUESTION: {
    title: 'Question',
    label: 'When user says',
    component: AiDialogueAction,
    types: ['AI_QUESTION', 'NPC_AI_QUESTION'],
    options: {
      props: {
      }
    },
    caseSpecific: true,
    isRecommended: ControlUtils.areAiQuestionsRecommended,
    isDisabled: ControlUtils.isAiDisabled,
    category: 'Dialogue',
    superCategory: 'Assess',
  },
  COMMAND: {
    title: 'Command',
    label: 'When command given',
    component: DialogueAction,
    options: {
      props: {
        actionType: 'COMMAND',
        placeholder: 'Command',
        getDialogueItems: (caseConf) => caseConf.dialogue && caseConf.dialogue.commands
      }
    },
    caseSpecific: true,
    isRecommended: ControlUtils.areCommandsRecommended,
    isDisabled: ControlUtils.isAiEnabled,
    category: 'Dialogue',
    superCategory: 'Assess',
  },
  CALL: {
    title: 'Call',
    label: 'When call made',
    component: CallAction,
    options: {
      props: {
      }
    },
    isRecommended: (caseConf) => caseConf.dialogue && caseConf.dialogue.calls && caseConf.dialogue.calls.length,
    category: 'Dialogue',
    superCategory: 'Assess',
  },
  OXYGEN: {
    title: 'Oxygen',
    label: 'When oxygen administered',
    component: OxygenAction,
    options: {
      query: require('~lib/common/queries/Oxygen').fetch,
      queryProp: 'oxygens',
    },
    isRecommended: (caseConf) => !!getEnabledRelations(caseConf, '_oxygens').length,
    category: 'Oxygen',
    superCategory: 'Treat',
  },
  MONITOR_VITALS_ON: {
    title: 'Monitor',
    label: 'When connected to Monitor',
    component: MonitorVitalsAction,
    options: {
      props: {
        valueBool: true,
      },
    },
    type: 'VITALS',
    isApplicable: action => action.category === 'monitor' && action.valueBool === true,
    category: 'Monitors',
    superCategory: 'Monitor',
  },
  MONITOR_LEADS_ON: {
    title: 'ECG',
    label: 'When connected to ECG',
    component: MonitorLeadsAction,
    options: {
      query: require('~lib/common/queries/Treatment').fetch,
      queryProp: 'treatments',
      props: {
        valueBool: true,
      },
    },
    type: 'LEADS',
    isApplicable: action => action.valueBool === true,
    isRecommended: (caseConf) => !!getEnabledRelations(caseConf, '_treatments').find(t => t.id === 'LEAD_SELECT'),
    category: 'Monitors',
    superCategory: 'Monitor',
  },
  TAKE_VITAL_BLOOD_PRESSURE: {
    title: 'BP',
    label: 'When blood pressure taken',
    component: TakeVitalAction,
    options: {
      props: {
        typeID: 'BLOOD_PRESSURE_BOTH',
        valueBool: true,
      },
    },
    type: 'VITALS',
    isApplicable: action => action.id === 'BLOOD_PRESSURE_BOTH',
    category: 'Patient Conditions',
    superCategory: 'Monitor',
  },
  TAKE_VITAL_TEMPERATURE: {
    title: 'Temperature',
    label: 'When temperature taken',
    component: TakeVitalAction,
    options: {
      props: {
        typeID: 'TEMPERATURE',
        valueBool: true,
      },
    },
    type: 'VITALS',
    isApplicable: action => action.id === 'TEMPERATURE',
    category: 'Patient Conditions',
    superCategory: 'Monitor',
  },
  IV_PLACED: {
    title: 'IV',
    label: 'When IV placed',
    component: IvPlacedAction,
    options: {
      props: {
        typeID: 'IV',
        valueBool: true,
      },
    },
    type: 'IV',
    isApplicable: action => action.id === 'IV' && action.valueBool === true,
    isRecommended: (caseConf) => caseConf.iv && caseConf.iv.enabled,
    category: 'IV',
    superCategory: 'Treat',
  },
  IV_REMOVED: {
    title: 'IV',
    label: 'When IV removed',
    component: IvPlacedAction,
    options: {
      props: {
        typeID: 'IV',
        valueBool: false,
      },
    },
    type: 'IV',
    isApplicable: action => action.id === 'IV' && action.valueBool === false,
    isRecommended: (caseConf) => caseConf.iv && caseConf.iv.enabled,
    category: 'IV',
    superCategory: 'Treat',
  },
  INTERVENTION: {
    title: 'Intervention',
    label: 'When intervention administered',
    component: InterventionAction,
    isRecommended: (caseConf) => !!caseConf.availableInterventions && !!caseConf.availableInterventions.length,
    category: 'Intervention',
    superCategory: 'Treat',
    options: {
      query: require('~lib/common/queries/State').fetch,
      queryProp: 'states',
    },
  },
  MEDICATION: {
    title: 'Medication',
    label: 'When medication administered',
    component: MedicationAction,
    options: {
      query: require('~lib/common/queries/Medication').fetch,
      queryProp: 'medications',
    },
    isRecommended: (caseConf) => caseConf.dosageForms && caseConf.dosageForms.length,
    category: 'Medications',
    superCategory: 'Treat',
  },
  INFUSION: {
    title: 'Infusion',
    label: 'When infusion administered/adjusted',
    component: InfusionAction,
    types: ['INFUSION_MEDICATION', 'INFUSION_FLUID'],
    options: {
      query: require('~lib/common/queries/Medication').fetch,
      queryProp: 'medications',
    },
    isRecommended: caseConf =>
      caseConf.dosageForms &&
      caseConf.dosageForms.find(caseDosageForm =>
        caseDosageForm.enabledRoutes.find(r => r.key === 'IV_IO_INFUSION'),
      ),
    category: 'Medications',
    superCategory: 'Treat',
  },
  COMPRESSION: {
    title: 'CPR',
    label: 'When compressions administered',
    component: CompressionAction,
    isRecommended: (caseConf) => !!getEnabledRelations(caseConf, '_treatments').find(t => t.id === 'CPR'),
    category: 'Procedures' ,
    superCategory: 'Treat',
  },
  DEFIB: {
    title: 'Defibrillation',
    label: 'When defibrillated',
    component: DefibAction,
    type: 'PACING',
    isApplicable: action => action.id === 'SYNC' || action.id === 'DEFIBRILLATION',
    isRecommended: (caseConf) => !!getEnabledRelations(caseConf, '_treatments').find(t => t.id === 'DEFIBRILLATION'),
    category: 'Monitors',
    superCategory: 'Monitor',
  },
  PACING_ON: {
    title: 'Pacing',
    label: 'When pacing turned on',
    component: PacingAction,
    options: {
      props: {
        valueBool: true,
      },
    },
    type: 'PACING',
    isApplicable: action => action.id === 'TRANSCUTANEOUS' && action.valueBool === true,
    isRecommended: (caseConf) => !!getEnabledRelations(caseConf, '_treatments').find(t => t.id === 'PACING'),
    category: 'Monitors',
    superCategory: 'Monitor',
  },
  MONITOR_VITALS_OFF: {
    title: 'Monitor',
    label: 'When disconnected from Monitor',
    component: MonitorVitalsAction,
    options: {
      props: {
        valueBool: false,
      },
    },
    type: 'VITALS',
    isApplicable: action => action.category === 'monitor' && action.valueBool === false,
    category: 'Monitors',
    superCategory: 'Monitor',
  },
  MONITOR_LEADS_OFF: {
    title: 'ECG',
    label: 'When disconnected from ECG',
    component: MonitorLeadsAction,
    options: {
      query: require('~lib/common/queries/Treatment').fetch,
      queryProp: 'treatments',
      props: {
        valueBool: false,
      },
    },
    type: 'LEADS',
    isApplicable: action => action.valueBool === false,
    isRecommended: (caseConf) => !!getEnabledRelations(caseConf, '_treatments').find(t => t.id === 'LEAD_SELECT'),
    category: 'Monitors',
    superCategory: 'Monitor',
  },
  PACING_OFF: {
    title: 'Pacing',
    label: 'When pacing turned off',
    component: PacingAction,
    options: {
      props: {
        valueBool: false,
      },
    },
    type: 'PACING',
    isApplicable: action => action.id === 'TRANSCUTANEOUS' && action.valueBool === false,
    isRecommended: (caseConf) => !!getEnabledRelations(caseConf, '_treatments').find(t => t.id === 'PACING'),
    category: 'Monitors',
    superCategory: 'Monitor',
  },
  PENLIGHT: {
    title: 'Penlight',
    label: 'When penlight is used',
    component: PenlightAction,
    type: 'ASSESSMENT',
    isApplicable: action => action.id === 'PENLIGHT',
    category: 'Patient Conditions',
    superCategory: 'Assess',
  },
  VAGAL_MANEUVER: {
    title: 'Vagal Maneuver',
    label: 'When vagal maneuver performed',
    component: VagalManeuverAction,
    type: 'PROCEDURE',
    isApplicable: action => action.id === 'VAGAL_MANEUVER',
    isRecommended: (caseConf) => !!getEnabledRelations(caseConf, '_treatments').find(t => t.id === 'VAGAL_MANEUVER'),
    category: 'Procedures',
    superCategory: 'Treat',
  },
  NASAL_SWAB: {
    title: 'Nasal Swab',
    label: 'When nasal swab taken',
    component: NasalSwabAction,
    type: 'PROCEDURE',
    isApplicable: action => action.id === 'NASAL_SWAB',
    isRecommended: (caseConf) => !!getEnabledRelations(caseConf, '_treatments').find(t => t.id === 'NASAL_SWAB'),
    category: 'Procedures',
    superCategory: 'Treat',
  },
  TRANSFER: {
    title: 'Transfer',
    label: 'When patient transferred',
    component: PatientTransferAction,
    options: {
      props: {
        valueBool: true,
      },
    },
    isRecommended: (caseConf) => caseConf._endLocation,
    category: 'Transport',
    superCategory: 'Treat',
  },
  PERSONAL_PROTECTIVE_EQUIPMENT: {
    title: 'PPE',
    label: 'When PPE used',
    component: PPEAction,
    isRecommended: (caseConf) => !!getEnabledRelations(caseConf, '_treatments').find(t => t.id === 'PERSONAL_PROTECTIVE_EQUIPMENT'),
    category: 'Procedures',
    superCategory: 'Assess',
  },
  ADJUST_BED_RAIL: {
    title: 'Bed rail',
    label: 'When adjusting bed rail',
    component: BedRailAction,
    type: 'PROCEDURE',
    isApplicable: action => action.id === 'RAISE_BED_RAIL' || action.id === 'LOWER_BED_RAIL',
    isRecommended: (caseConf) =>
      (caseConf._startLocation && ['CLINIC', 'HOSPITAL', 'EMERGENCY_ROOM'].includes(caseConf._startLocation.key)) ||
      (caseConf._endLocation && ['CLINIC', 'HOSPITAL', 'EMERGENCY_ROOM'].includes(caseConf._endLocation.key)),
    category: 'Procedures',
    superCategory: 'Treat',
  },
  EXPOSED: {
    title: 'Expose',
    label: 'When patient clothes',
    component: ExposeAction,
    type: 'PROCEDURE',
    isApplicable: action => action.id && (action.id.includes('_REPLACED') || action.id.includes('_REMOVED')),
    category: 'Procedures',
    superCategory: 'Assess',
  },
  STETHOSCOPE: {
    title: 'Stethoscope',
    label: 'When stethoscope used',
    component: StethoscopeAction,
    type: 'ASSESSMENT',
    isApplicable: action => action.id === 'HEART_SOUNDS' || action.id === 'LUNG_SOUNDS',
    category: 'Patient Conditions',
    superCategory: 'Assess',
  },
  PALPABLE_PULSE: {
    title: 'Palpate Pulse',
    label: 'When pulse palpated',
    component: PalpatePulseAction,
    category: 'Patient Conditions',
    superCategory: 'Assess',
  },
  SPINAL_ASSESSMENT: {
    title: 'Spinal Assessment',
    label: 'When spine assessed',
    component: SpinalAssessmentAction,
    isRecommended: (caseConf) => (caseConf.spinalAssessments || []).length > 0,
    category: 'Patient Conditions',
    superCategory: 'Assess',
  },
  CUSTOM_MENU: {
    title: 'Custom Menu',
    label: 'When custom menu selected',
    component: CustomMenuAction,
    caseSpecific: true,
    isRecommended: (caseConf) => (caseConf.customMenus || []).length > 0,
    category: 'Dialogue',
    superCategory: 'Miscellaneous',
  },
  GLUCOSE: {
    title: 'Glucose',
    label: 'When glucose checked',
    component: GlucoseAction,
    type: 'LAB_RESULT_VIEW',
    isApplicable: action => action.id === 'GLUCOSE',
    category: 'Patient Conditions',
    superCategory: 'Monitor',
  },
  PHYSICAL: {
    title: 'Physical',
    label: 'When patient assessed',
    component: PhysicalAssessmentAction,
    type: 'ASSESSMENT',
    options: {
      query: require('~lib/common/queries/State').fetch,
      queryProp: 'states',
    },
    isApplicable: action => action.id === 'PHYSICAL',
    category: 'Patient Conditions',
    superCategory: 'Assess',
  },
  NPC_DIALOGUE: {
    title: 'NPC Dialogue',
    label: 'NPC Dialogue',
    component: NpcAction,
    types: ['NPC_QUESTION', 'NPC_COMMAND'],
    isApplicable: action => ['NPC_QUESTION', 'NPC_COMMAND'].includes(action.type),
    isDisabled: ControlUtils.isAiEnabled,
    isRecommended: ControlUtils.isNpcDialogueRecommended,
    category: 'Dialogue',
    superCategory: 'Miscellaneous',
  },
  CUSTOM_STATE: {
    title: 'Custom State Event',
    label: 'When custom state changes',
    component: CustomStateEvent,
    options: {
      props: {
        type: 'CUSTOM_STATE',
      },
    },
    isRecommended: ControlUtils.areCustomStatesRecommended,
    category: 'Custom State Change',
    superCategory: 'Miscellaneous',
  },
  CUSTOM_ENUM_STATE: {
    title: 'Custom Enum State Event',
    label: 'When custom enum state changes',
    component: CustomStateEvent,
    options: {
      props: {
        type: 'CUSTOM_ENUM_STATE',
      },
    },
    isRecommended: ControlUtils.areCustomEnumStatesRecommended,
    category: 'Custom Enum State Change',
    superCategory: 'Miscellaneous',
  },
};

const ACTION_CONTROLS = mapValues(ACTIONS, action => ({
  ...action,
  control: makeVueControl(action.component, action.options),
}));

export default ACTION_CONTROLS;
